import React, { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "";

const FORM_SOURCES = [
  {
    id: "packages",
    label: "Packages",
    description: "Inquire & Customize popup on the Packages section.",
    endpoint: "/api/packages/inquiries",
    fileName: "packages-enquiries",
    columns: [
      { key: "packageTitle", label: "Package" },
      { key: "tabLabel", label: "Tab" },
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "date", label: "Preferred Date" },
      { key: "guests", label: "Guests" },
      { key: "createdAt", label: "Submitted At", format: "datetime" },
    ],
  },
  {
    id: "browse-packages",
    label: "Browse Packages",
    description: "Book Now popup on the Browse Packages section.",
    endpoint: "/api/browse-packages/inquiries",
    fileName: "browse-packages-enquiries",
    columns: [
      { key: "packageTitle", label: "Package" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "date", label: "Preferred Date" },
      { key: "guests", label: "Guests" },
      { key: "createdAt", label: "Submitted At", format: "datetime" },
    ],
  },
];

const formatCell = (row, column) => {
  const value = row[column.key];

  if (value === undefined || value === null || value === "") {
    return "";
  }

  if (column.format === "datetime") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  }

  return String(value);
};

const escapeXml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const downloadExcel = (fileName, sheetName, headers, rows) => {
  const headerXml = `<Row>${headers
    .map(
      (header) =>
        `<Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXml(
          header
        )}</Data></Cell>`
    )
    .join("")}</Row>`;

  const bodyXml = rows
    .map(
      (row) =>
        `<Row>${row
          .map(
            (cell) =>
              `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`
          )
          .join("")}</Row>`
    )
    .join("");

  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Header">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#E5E7EB" ss:Pattern="Solid"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="${escapeXml(sheetName.slice(0, 31))}">
  <Table>
   ${headerXml}
   ${bodyXml}
  </Table>
 </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], {
    type: "application/vnd.ms-excel",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const Enquiries = () => {
  const [selectedFormId, setSelectedFormId] = useState(FORM_SOURCES[0].id);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const selectedForm = useMemo(
    () =>
      FORM_SOURCES.find((form) => form.id === selectedFormId) ||
      FORM_SOURCES[0],
    [selectedFormId]
  );

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("indiaboatsAdminToken");
        const response = await fetch(
          `${API_BASE_URL}${selectedForm.endpoint}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to load enquiries");
        }

        const result = await response.json();
        setRows(Array.isArray(result) ? result : []);
      } catch (fetchError) {
        console.error("Enquiries fetch error:", fetchError);
        setRows([]);
        setError(fetchError.message || "Failed to load enquiries");
      } finally {
        setLoading(false);
      }
    };

    fetchInquiries();
  }, [selectedForm]);

  const handleDownloadExcel = () => {
    if (!rows.length) {
      alert("No enquiries to download for this form.");
      return;
    }

    const headers = selectedForm.columns.map((column) => column.label);
    const excelRows = rows.map((row) =>
      selectedForm.columns.map((column) => formatCell(row, column))
    );

    downloadExcel(
      selectedForm.fileName,
      selectedForm.label,
      headers,
      excelRows
    );
  };

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Enquiries</h1>
          <p>
            View popup form submissions by source, then download the table as
            Excel.
          </p>
        </div>

        <button
          type="button"
          className="cms-save-btn"
          onClick={handleDownloadExcel}
          disabled={loading || !rows.length}
        >
          Download Excel
        </button>
      </div>

      <div className="cms-packages-layout">
        <div className="cms-packages-main">
          <div className="cms-section-card">
            <div className="cms-section-card-header">
              <h2>{selectedForm.label}</h2>
              <p>{selectedForm.description}</p>
            </div>

            {loading ? (
              <p className="cms-inquiries-empty">Loading enquiries...</p>
            ) : error ? (
              <p className="cms-inquiries-empty">{error}</p>
            ) : rows.length === 0 ? (
              <p className="cms-inquiries-empty">
                No enquiries submitted for this form yet.
              </p>
            ) : (
              <div className="cms-enquiries-table-wrap">
                <table className="cms-enquiries-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {selectedForm.columns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{index + 1}</td>
                        {selectedForm.columns.map((column) => (
                          <td key={column.key}>{formatCell(row, column)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <aside className="cms-inquiries-panel">
          <div className="cms-section-card">
            <div className="cms-section-card-header">
              <h2>Select Form</h2>
              <p>Choose which popup form submissions to view.</p>
            </div>

            <div className="cms-enquiry-form-list">
              {FORM_SOURCES.map((form) => (
                <button
                  type="button"
                  key={form.id}
                  className={`cms-enquiry-form-option${
                    form.id === selectedFormId ? " is-active" : ""
                  }`}
                  onClick={() => setSelectedFormId(form.id)}
                >
                  <strong>{form.label}</strong>
                  <span>{form.description}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Enquiries;
